'use client'

import { type ChangeEventHandler, type MouseEvent, type MouseEventHandler, useId, useState } from 'react'
import clsx from 'clsx'
import { LoadingDots } from 'ui/src/components/LoadingDots/LoadingDots'
import { Text } from 'ui/src/components/Text/Text'
import { MinusIcon } from 'ui/src/icons/MinusIcon'
import { PlusIcon } from 'ui/src/icons/PlusIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { tokens } from 'ui/src/theme/tokens.css'
import { useHighlightAnimation } from 'ui/src/utils/useHighlightAnimation'
import { outerWrapper, select, selectLoading, stepButton } from './StepperInput.css'

export type StepperInputProps = {
  name?: string
  autoFocus?: boolean
  min?: number
  max: number
  value?: number
  defaultValue?: number
  required?: boolean
  label?: string
  loading?: boolean
  disabled?: boolean
  optionLabel?: (count: number) => string
  onChange?: (value: number) => void
  size?: 'small' | 'medium' | 'large'
  className?: string
}

/**
 * Specialized Stepper Input
 * Based on: https://www.magentaa11y.com/checklist-web/stepper-input/
 */
export function StepperInput(props: StepperInputProps) {
  const {
    max,
    min = 0,
    value,
    defaultValue,
    label,
    loading,
    disabled,
    optionLabel = (count) => count,
    size = 'medium',
    className,
    ...inputProps
  } = props
  const [internalValue, setInternalValue] = useState(value ?? defaultValue ?? min)
  const { highlight, animationProps } = useHighlightAnimation<HTMLDivElement>()

  const selectId = useId()
  const labelId = useId()

  const changeValue = (event: MouseEvent, amount: 1 | -1) => {
    event.preventDefault()
    if (disabled || loading) return
    highlight()

    const nextValue = Math.min(max, Math.max(min, internalValue + amount))
    setInternalValue(nextValue)
    props.onChange?.(nextValue)
  }

  const increment: MouseEventHandler = (event) => {
    changeValue(event, 1)
  }

  const decrement: MouseEventHandler = (event) => {
    changeValue(event, -1)
  }

  const handleChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
    if (disabled || loading) return
    const numberValue = parseInt(event.target.value, 10)
    setInternalValue(numberValue)
    props.onChange?.(numberValue)
  }

  const options = Array.from({ length: max + 1 }, (_, count) => ({
    label: optionLabel(count),
    value: count,
  }))

  const isDisabled = disabled || loading
  const isDecrementDisabled = internalValue === min || isDisabled
  const isIncrementDisabled = internalValue === max || isDisabled

  return (
    <div className={clsx(outerWrapper[size], className)} {...animationProps}>
      <div className={yStack({ flexGrow: 1 })}>
        {label && (
          <label id={labelId} htmlFor={selectId}>
            <Text
              as="span"
              size="xs"
              color={isDisabled ? 'textTertiary' : 'textTranslucentSecondary'}
              singleLine={true}
            >
              {label}
            </Text>
          </label>
        )}

        <select
          {...inputProps}
          id={selectId}
          className={clsx(select[size], isDisabled && selectLoading)}
          value={internalValue}
          onChange={handleChange}
          aria-labelledby={labelId}
          disabled={isDisabled}
        >
          {options.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className={xStack({ gap: 'md' })}>
        {loading ? (
          <div>
            <LoadingDots color={tokens.colors.fills.opaque.primary} />
          </div>
        ) : (
          <>
            <button
              className={stepButton}
              type="button"
              onClick={decrement}
              tabIndex={-1}
              aria-hidden={true}
              disabled={isDecrementDisabled}
            >
              <MinusIcon size="1rem" />
            </button>
            <button
              className={stepButton}
              type="button"
              onClick={increment}
              tabIndex={-1}
              aria-hidden={true}
              disabled={isIncrementDisabled}
            >
              <PlusIcon size="1rem" />
            </button>
          </>
        )}
      </div>
    </div>
  )
}
